import { t } from '@lingui/macro'
import {
  createBudgetUpdateParsed,
  parseBudgetUpdateSFObject,
  updateBudgetUpdateParsed
} from 'app/services/sfAuth/sfData/reports/sfBudgetUpdate'
import { saveReportParsed } from 'app/services/sfAuth/sfData/sfReports'
import { getFieldMaxLength, getFieldScale } from 'app/utils/salesforceFieldUtils'
import currency from 'currency.js'
import _ from 'lodash'
import { myI18n } from 'translation/I18nConnectedProvider'
import { isNumberOrZero } from 'utils'
import { isFalsy, isTruthy } from 'utilsTS'
import { budgetCentreGrantKey, sanitizeLineKey, WHOLE_BUDGET } from '../budget/FormBudget'
import { BudgetUpdateActual, BudgetUpdateChange, BudgetUpdateLines, FormBudgetUpdateValue } from './budgetUpdateTypes'
import './FormBudgetUpdate.css'

export const budgetUpdateKeys = [
  { key: 'applicationBudget', subKey: 'value' },
  { key: 'latestUpdate', subKey: 'change' },
  { key: 'budgetUpdateChange', subKey: 'change' },
  { key: 'budgetUpdateActual', subKey: 'actual' }
] as const

export const isLatestUpdateValid = (value: FormBudgetUpdateValue): boolean =>
  Object.values({ ...value.expensesLines, ...value.revenuesLines }).some(
    ({ applicationBudget, latestUpdate }) => {
      if (isTruthy(latestUpdate.change)) {
        return latestUpdate.change !== applicationBudget.value
      }
      return false
    }
  )

interface BudgetUpdateDefaultValueOptions {
  enqueueSnackbar: (message: string, options?: object) => void
  disabledFromSFConditions?: string[]
}

interface SFObject {
  Opportunity?: any
  FGM_Base__Grantee_Report__c?: any
}

interface FormBudgetUpdateItem {
  id: string
}

export const formBudgetUpdateDefaultValue = (
  obj: SFObject,
  { enqueueSnackbar, disabledFromSFConditions = [] }: BudgetUpdateDefaultValueOptions,
  item: FormBudgetUpdateItem
): FormBudgetUpdateValue | {} => {
  const opportunityObj = obj.Opportunity
  const reportObj = obj.FGM_Base__Grantee_Report__c
  const toRet: Partial<FormBudgetUpdateValue> = {}
  const categoryInfoMapping: Record<
  string, {
    lineKey: string
    categoryId: string
    isExpense: boolean
  }> = {}
  let latestUpdateIsASourceOfDefaultValue = false

  if (isFalsy(opportunityObj) || isFalsy(reportObj) || disabledFromSFConditions.includes(item.id)) {
    return toRet
  }

  const allTimeOpportunityGranteeBudgetLines = opportunityObj.FGM_Portal__Grantee_Budget_Line_Items__r?.records
    .filter(record => record.FGM_Portal__Grantee_Budget__r?.Name === WHOLE_BUDGET)

  if (isTruthy(opportunityObj)) {
    if (allTimeOpportunityGranteeBudgetLines.length === 0) {
      throw new Error('BUDGET_UPDATE_NO_LINES')
    }
    const noCategory =
      allTimeOpportunityGranteeBudgetLines.some(
        (obj) => isFalsy(obj.FGM_Portal__Category__r)
      )
    if (noCategory === true) {
      enqueueSnackbar(
        myI18n._(t`LOADING_ERROR_BUDGET_UPDATE_NO_PARENT_CATEGORY.`),
        {
          variant: 'error'
        }
      )
      return toRet
    }
    allTimeOpportunityGranteeBudgetLines.forEach(
      (obj) => {
        const type =
          obj.FGM_Portal__Category__r?.FGM_Portal__Parent_Category__r.Name
        const value = (obj.FGM_Portal__Amount__c ?? 0) as number
        const lineKey = sanitizeLineKey(obj.FGM_Portal__Category__r.Name) as string
        const comment = obj.FGM_Portal__Note__c
        const isExpense = type === 'Expenses'
        const categoryId = obj.FGM_Portal__Category__r.Id
        const order = obj.FGM_Portal__Category__r.Order__c
        categoryInfoMapping[categoryId] = {
          lineKey,
          categoryId,
          isExpense
        }

        const targetPath = isExpense
          ? `expensesLines.${lineKey}`
          : `revenuesLines.${lineKey}`

        const existingData = _.get(toRet, `${targetPath}`)

        // We are setting the base line value based on first budget line of given category
        if (isFalsy(existingData)) {
          _.set(toRet, targetPath, {
            categoryId,
            order,
            defaultValue: {
              value,
              comment
            },
            applicationBudget: {
              value,
              comments: isTruthy(comment) ? [comment] : []
            }
          })
        } else {
          existingData.applicationBudget.value = (existingData.applicationBudget.value as number) + value
          existingData.defaultValue.value = (existingData.defaultValue.value as number) + value

          if (isTruthy(comment)) {
            existingData.applicationBudget.comments.push(comment)
          }
          _.set(toRet, targetPath, existingData)
        }
      }
    )

    const latestReport = opportunityObj.granteeReports.find(report => {
      return Object.entries({ ...toRet.expensesLines, ...toRet.revenuesLines }).some(
        ([lineKey, { applicationBudget }]) => {
          const updateLine = report.budgetUpdates.find(update => lineKey === categoryInfoMapping[update.budgetCategory]?.lineKey)
          return Boolean(isTruthy(updateLine?.change) && (updateLine.change !== applicationBudget.value))
        }
      )
    })

    if (latestReport?.budgetUpdates?.length > 0) {
      toRet.latestReport = latestReport
      latestUpdateIsASourceOfDefaultValue = true
      latestReport.budgetUpdates
        .filter((obj) => categoryInfoMapping[obj.budgetCategory])
        .forEach((budgetUpdate) => {
          const { lineKey, isExpense } =
              categoryInfoMapping[budgetUpdate.budgetCategory]
          const targetPath = isExpense
            ? `expensesLines.${lineKey}`
            : `revenuesLines.${lineKey}`
          _.set(toRet, targetPath + '.latestUpdate', budgetUpdate)
          _.set(toRet, targetPath + '.defaultValue', {
            value: budgetUpdate.change ?? 0,
            comment: budgetUpdate.changeDetails
          })
        })
    }
  }

  if (isTruthy(reportObj)) {
    toRet.report = {
      id: reportObj.Id,
      status: reportObj.FGM_Base__Status__c,
      hasBudgetChanged: reportObj.Budget_changes__c ?? 'Yes'
    }

    if (isTruthy(reportObj.Budget_Updates__r)) {
      reportObj.Budget_Updates__r.records.forEach((sfBudgetUpdate) => {
        const budgetUpdate = parseBudgetUpdateSFObject(sfBudgetUpdate)
        if (isTruthy(categoryInfoMapping[budgetUpdate.budgetCategory])) {
          const { lineKey, isExpense } =
              categoryInfoMapping[budgetUpdate.budgetCategory]

          const changeTargetPath = isExpense
            ? `expensesLines.${lineKey}.budgetUpdateChange`
            : `revenuesLines.${lineKey}.budgetUpdateChange`

          _.set(toRet, changeTargetPath, {
            id: budgetUpdate.id,
            change: budgetUpdate.change ?? 0,
            changeDetails: budgetUpdate.changeDetails
          })

          const actualTargetPath = isExpense
            ? `expensesLines.${lineKey}.budgetUpdateActual`
            : `revenuesLines.${lineKey}.budgetUpdateActual`
          let actual = budgetUpdate.actual
          if (lineKey === budgetCentreGrantKey && !isNumberOrZero(actual)) {
            actual = opportunityObj.Actual_Payments_Made__c
          }
          _.set(toRet, actualTargetPath, {
            id: budgetUpdate.id,
            actual: actual ?? 0,
            actualComments: budgetUpdate.actualComments
          })
        } else {
          console.warn(
            'There is no budget line on Opportunity matching budget category of existing budget update object!'
          )
        }
      })
    } else {
      _.set(toRet, `revenuesLines.${budgetCentreGrantKey}.budgetUpdateActual`, {
        actual: opportunityObj.Actual_Payments_Made__c ?? 0,
        actualComments: ''
      })
    }
  }

  // Add default values to not created budget update lines

  if (isTruthy(toRet.expensesLines) && toRet.expensesLines != null) {
    Object.entries(toRet.expensesLines).forEach(([lineKey, lineData]) => {
      const { budgetUpdateChange, applicationBudget, latestUpdate } = lineData
      const useLatest = isTruthy(latestUpdate) && latestUpdateIsASourceOfDefaultValue
      if (isFalsy(budgetUpdateChange)) {
        const change = useLatest ? latestUpdate.change : applicationBudget.value
        _.set(toRet, `expensesLines.${lineKey}.budgetUpdateChange`, {
          change: change ?? 0,
          changeDetails: useLatest
            ? latestUpdate.changeDetails
            : Array.isArray(applicationBudget.comments)
              ? applicationBudget.comments.join('\n')
              : ''
        })
      }
    })
  }

  if (isTruthy(toRet.revenuesLines) && toRet.revenuesLines != null) {
    Object.entries(toRet.revenuesLines).forEach(([lineKey, lineData]) => {
      const { budgetUpdateChange, applicationBudget, latestUpdate } = lineData
      const useLatest = isTruthy(latestUpdate) && latestUpdateIsASourceOfDefaultValue
      if (isFalsy(budgetUpdateChange)) {
        const change = useLatest ? latestUpdate.change : applicationBudget.value
        _.set(toRet, `revenuesLines.${lineKey}.budgetUpdateChange`, {
          change: change ?? 0,
          changeDetails: useLatest
            ? latestUpdate.changeDetails
            : Array.isArray(applicationBudget.comments)
              ? applicationBudget.comments.join('\n')
              : ''
        })
      }
    })
  }
  // If latest budget is either empty or the same as application budget we reset default value
  if (!latestUpdateIsASourceOfDefaultValue) {
    Object.entries({
      ...toRet.revenuesLines,
      ...toRet.expensesLines
    }).forEach(([lineKey, lineData]) => {
      const { applicationBudget } = lineData
      const prefix =
        toRet.revenuesLines != null &&
        lineKey in toRet?.revenuesLines
          ? 'revenuesLines'
          : 'expensesLines'
      const value = applicationBudget.value ?? 0
      const comment = Array.isArray(applicationBudget.comments)
        ? applicationBudget.comments.join('\n')
        : applicationBudget.comments
      _.set(toRet, `${prefix}.${lineKey}.defaultValue`, {
        value,
        comment
      })
    })
  }

  return toRet
}

interface FormBudgetUpdateSavePromiseProps {
  value: FormBudgetUpdateValue
}

interface FormBudgetUpdateSavePromiseReturn {
  promise: Promise<any[]>
}

export const formBudgetUpdateSavePromise = ({
  value
}: FormBudgetUpdateSavePromiseProps): FormBudgetUpdateSavePromiseReturn => {
  const updatesToUpdate: Array<Partial<BudgetUpdateActual & BudgetUpdateChange>> = []
  const updatesToCreate: Array<Partial<
  BudgetUpdateActual &
  BudgetUpdateChange & {
    budgetCategory: string
    granteeReport: string
  }>> = []
  const dataArray = [
    ...Object.values(value.expensesLines),
    ...Object.values(value.revenuesLines)
  ]
  dataArray.forEach((dataObj) => {
    const { budgetUpdateChange, budgetUpdateActual = {}, categoryId } = dataObj
    const toUpdate = { ...budgetUpdateActual, ...budgetUpdateChange }
    const reportId = value.report.id
    if (isTruthy(toUpdate.id)) {
      delete toUpdate.granteeReport
      updatesToUpdate.push(toUpdate)
    } else {
      updatesToCreate.push({
        ...toUpdate,
        budgetCategory: categoryId,
        granteeReport: reportId
      })
    }
  })

  const reportToSave = { ...value.report }
  delete reportToSave.status
  const promises = [
    saveReportParsed(reportToSave)
  ]

  if (updatesToCreate.length > 0) {
    promises.push(createBudgetUpdateParsed(updatesToCreate))
  }
  if (updatesToUpdate.length > 0) {
    promises.push(updateBudgetUpdateParsed(updatesToUpdate))
  }

  return { promise: Promise.all(promises) }
}

export const isBudgetDifferenceZero = (value: number): boolean => Math.abs(value) <= 0.001

export const isBudgetUpdateChangeEqual = (
  revenuesLines: BudgetUpdateLines,
  expensesLines: BudgetUpdateLines
): boolean => {
  const revenueSum = Object.values(revenuesLines ?? {}).reduce(
    (acc, line) => {
      const value = line.budgetUpdateChange?.change
      return acc.add(currency(Number(value ?? 0), { precision: 2 }))
    },
    currency(0, { precision: 2 })
  )

  const expenseSum = Object.values(expensesLines ?? {}).reduce(
    (acc, line) => {
      const value = line.budgetUpdateChange?.change
      return acc.add(currency(Number(value ?? 0), { precision: 2 }))
    },
    currency(0, { precision: 2 })
  )
  const budgetDifference = revenueSum.subtract(expenseSum).value

  return isBudgetDifferenceZero(budgetDifference)
}

export const getBudgetUpdateFieldLimits = (describeMap) => {
  return {
    amountChangesLength: getFieldMaxLength(
      describeMap,
      'Budget_Update__c',
      'Amount_changes__c'
    ),
    amountChangesDecimalScale: getFieldScale(
      describeMap,
      'Budget_Update__c',
      'Amount_changes__c'
    ),
    changeDetailsLength: getFieldMaxLength(
      describeMap,
      'Budget_Update__c',
      'Change_details__c'
    ),
    actualAmountLength: getFieldMaxLength(
      describeMap,
      'Budget_Update__c',
      'Actual_amount__c'
    ),
    actualAmountDecimalScale: getFieldScale(
      describeMap,
      'Budget_Update__c',
      'Actual_amount__c'
    ),
    actualAmountCommentsLength: getFieldMaxLength(
      describeMap,
      'Budget_Update__c',
      'Actual_Amount_Comments__c'
    )
  }
}
